import { useState } from 'react';
import {
  PasswordRequirements,
  usePasswordRequirements,
} from 'src/SharedCustomHooks/UsePasswordRequirements/usePasswordRequirements';
import { useAuthFlowsAPI } from 'src/SharedCustomHooks/UseAuthFlowsAPI/useAuthFlowsAPI';

export type ChangePasswordStep = 'form' | 'submitting' | 'success';

export interface UseChangePasswordDialogueOrchestratorReturn {
  step: ChangePasswordStep;
  oldPassword: string;
  password: string;
  confirmPassword: string;
  requirements: PasswordRequirements;
  isPasswordValid: boolean;
  passwordsMismatch: boolean;
  canSubmit: boolean;
  updateOldPassword: (value: string) => void;
  updatePassword: (value: string) => void;
  updateConfirmPassword: (value: string) => void;
  submit: () => Promise<void>;
  reset: () => void;
}

export const useChangePasswordDialogueOrchestrator = (
  userId: string,
  email: string
): UseChangePasswordDialogueOrchestratorReturn => {
  const [step, setStep] = useState<ChangePasswordStep>('form');
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const { requirements, isValid: isPasswordValid } =
    usePasswordRequirements(password);

  const { changePassword } = useAuthFlowsAPI();

  const passwordsMismatch: boolean =
    confirmPassword.length > 0 && password !== confirmPassword;

  const canSubmit: boolean =
    oldPassword.length > 0 &&
    password.length > 0 &&
    confirmPassword.length > 0 &&
    isPasswordValid &&
    !passwordsMismatch;

  const updateOldPassword = (value: string): void => {
    setOldPassword(value);
  };

  const updatePassword = (value: string): void => {
    setPassword(value);
  };

  const updateConfirmPassword = (value: string): void => {
    setConfirmPassword(value);
  };

  const submit = async (): Promise<void> => {
    if (!canSubmit) {
      console.warn('useChangePasswordDialogueOrchestrator.submit called while canSubmit is false — submit button should have been disabled.');
      return;
    }

    setStep('submitting');

    const success: boolean = await changePassword({
      userId,
      email,
      oldPassword,
      newPassword: password,
    });

    if (success) {
      setStep('success');
      return;
    }

    setStep('form');
  };

  const reset = (): void => {
    setStep('form');
    setOldPassword('');
    setPassword('');
    setConfirmPassword('');
  };

  return {
    step,
    oldPassword,
    password,
    confirmPassword,
    requirements,
    isPasswordValid,
    passwordsMismatch,
    canSubmit,
    updateOldPassword,
    updatePassword,
    updateConfirmPassword,
    submit,
    reset,
  };
};
